import React, { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cardDialectApi, cardConsoleApi } from '../../services/iso8583Api';
import { cardEndpoint } from '../../utils/cardEndpoint';
import SupportContact from '../../components/SupportContact';

/** Page heading, in the style of the other corridors' pages. */
export function PageHeader({ icon: Icon, title, children }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <Icon className="w-6 h-6 text-primary-600" /> {title}
      </h1>
      {children && <p className="text-sm text-gray-500 mt-1">{children}</p>}
    </div>
  );
}

/** A white card with a small heading. */
export function Card({ title, icon: Icon, children, className = '' }) {
  return (
    <section className={`bg-white border border-gray-200 rounded-xl p-5 ${className}`}>
      {title && (
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          {Icon && <Icon className="w-4 h-4 text-primary-600" />} {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/** Monospaced block with a copy button. */
export function CodeBlock({ children, copy = true }) {
  const [copied, setCopied] = useState(false);
  const text = String(children);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="relative">
      <pre className="bg-gray-900 text-gray-100 text-xs font-mono rounded-lg p-3 pr-16 overflow-x-auto whitespace-pre">{text}</pre>
      {copy && (
        <button onClick={onCopy} type="button"
          className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-gray-700 text-gray-200 hover:bg-gray-600">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}

/** A simple table; rows are arrays of cells. */
export function Table({ head, rows, mono = [] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
            {head.map((h, i) => <th key={i} className="py-2 pr-4 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 align-top">
              {r.map((c, j) => (
                <td key={j} className={`py-2 pr-4 ${mono.includes(j) ? 'font-mono text-xs text-gray-800 whitespace-nowrap' : 'text-gray-700'}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * What a participant should know about the card endpoint shown: that this
 * deployment has not published one (the commands show placeholders), or that it
 * is a development deployment on this machine. Nothing when it is published.
 */
export function CardEndpointNote({ endpoint, className = '' }) {
  if (!endpoint.published) {
    return (
      <div className={`rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 ${className}`}>
        <p>
          This Sandbox has not published its card endpoint, so the commands on this page show
          <span className="font-mono"> &lt;card host&gt;</span> and <span className="font-mono">&lt;card port&gt;</span>.
        </p>
        <SupportContact body className="mt-1" lead="Ask AfricaNenda for them at"
          subject="AfricaNenda IIPS Sandbox card endpoint" />
      </div>
    );
  }
  if (endpoint.local) {
    return (
      <p className={`rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 ${className}`}>
        Development deployment: this card link runs on this machine. On the deployed Sandbox, connect to the endpoint
        its own portal shows here.
      </p>
    );
  }
  return null;
}

/**
 * The card link's endpoint, as the engine publishes it (`link` in /healthz) or
 * the portal's build names it; never guessed from the browser. See cardEndpoint.
 */
export function cardLink(health) {
  return cardEndpoint(health, import.meta.env.VITE_ISO8583_HOST);
}

/** Message type names, for tables and the ledger. */
export const MTI_NAMES = {
  '0100': 'Authorisation request', '0101': 'Authorisation request (repeat)', '0110': 'Authorisation response',
  '0120': 'Authorisation advice', '0121': 'Authorisation advice (repeat)', '0130': 'Authorisation advice response',
  '0200': 'Financial request', '0201': 'Financial request (repeat)', '0210': 'Financial response',
  '0220': 'Financial advice', '0221': 'Financial advice (repeat)', '0230': 'Financial advice response',
  '0400': 'Reversal request', '0410': 'Reversal response',
  '0420': 'Reversal advice', '0421': 'Reversal advice (repeat)', '0430': 'Reversal advice response',
  '0800': 'Network management request', '0810': 'Network management response',
};

/** The name of a message type in any revision: 1200 is named as 0200 is, by its last three digits. */
export function mtiName(mti) {
  if (!mti || String(mti).length !== 4) return '';
  return MTI_NAMES['0' + String(mti).slice(1)] || '';
}

const BASELINE_NM = {
  requestMti: '0800', responseMti: '0810', field: 70,
  codes: { signOn: '001', signOff: '002', echoTest: '301', pinKeyChange: '101', macKeyChange: '160', text: '999' },
};
const BASELINE_RC = {
  field: 39,
  codes: { approved: '00', invalidTransaction: '12', formatError: '30', institutionUnknown: '31',
    functionNotSupported: '40', securityViolation: '63', duplicateTransaction: '94', switchInoperative: '91',
    systemMalfunction: '96', macError: 'D1', insufficientFunds: '51', doNotHonour: '05', invalidCard: '14' },
};

/**
 * The dialect in force, with what a page needs to speak it: message types in
 * its version, its network-management convention, its response codes, and
 * whether it defines an element. Until it loads — or if it cannot — the 1987
 * baseline, so a page never renders blank.
 */
export function useDialect() {
  const [dialect, setDialect] = useState(null);
  useEffect(() => { cardDialectApi.get().then(setDialect).catch(() => setDialect(null)); }, []);
  const nm = dialect?.networkManagement || BASELINE_NM;
  const rc = dialect?.responseCodes || BASELINE_RC;
  const version = nm.requestMti[0];
  const defined = dialect?.fields ? new Set(dialect.fields.map((f) => f.de)) : null;
  return {
    dialect,
    id: dialect?.id || 'generic-ascii-1987',
    baseline: !dialect || dialect.id === 'generic-ascii-1987',
    /** A message type in this dialect's version, from its 1987 spelling: mti('0200') is 1200 under 1993. */
    mti: (base) => version + base.slice(1),
    nm,
    rc,
    /** The code this dialect uses for an outcome — 'approved', 'insufficientFunds', … */
    code: (outcome) => rc.codes[outcome] ?? '?',
    /** Whether the dialect defines an element; true when the table is not published, so nothing is hidden by guesswork. */
    defines: (de) => (defined ? defined.has(de) : true),
    /** The elements of a list this dialect defines — "DE 3, 4, 7, 11" less whatever it lacks. */
    only: (list) => list.filter((de) => (defined ? defined.has(de) : true)),
    reversal: dialect?.reversal || null,
    /** Which elements identify a transaction here. */
    identity: dialect?.transactionKey || { fields: [11, 7], optional: [32, 33] },
    /** How a reversal names its original: a packed element, or the original's own elements. */
    originalData: dialect?.originalData || { layout: 'iso1987', field: 90 },
  };
}

/**
 * A sign-on the engine built for this participant, in the dialect in force,
 * and read back with every element explained. Generated rather than written
 * into a page, so a sample can never drift from what the switch accepts.
 */
/** `refreshKey`: change it to build the sample again, e.g. after the institution id changed. */
export function useSignOnSample(refreshKey = 0) {
  const [sample, setSample] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const t = await cardConsoleApi.templates();
        const list = Array.isArray(t) ? t : (t.templates || []);
        const signOn = list.find((x) => x.id === 'sign-on');
        if (!signOn) throw new Error('no sign-on template');
        const fields = Object.fromEntries(signOn.fields.map((f) => [String(f.de), f.value]));
        const built = await cardConsoleApi.generate(signOn.mti, fields);
        if (!live) return;
        setSample({ ...built, mti: signOn.mti, template: signOn });
        const read = await cardConsoleApi.explain(built.frameHex);
        if (live) setExplanation(read?.error ? null : read);
      } catch (e) {
        if (live) setError(e.response?.data?.error || e.message);
      }
    })();
    return () => { live = false; };
  }, [refreshKey]);
  return { sample, explanation, error };
}

/** "DE 90", "DE 56", or "the original's DE 2, 37 and 12". */
export function originalElement(od) {
  if (!od) return 'DE 90';
  if (od.layout === 'fields') return `the original's ${listOf(od.match)}`;
  return `DE ${od.field}`;
}

/** What that element must carry, in a participant's terms. */
export function originalHow(od) {
  if (!od || od.layout === 'iso1987') return "the original's MTI, STAN and DE 7, then DE 32 and DE 33 zero-filled to 11 digits";
  if (od.layout === 'iso1993') return "the original's MTI, STAN and DE 12, then its acquirer id after a two-digit length";
  return `the original's ${listOf(od.match)}${od.fallback?.length ? `, or ${listOf(od.fallback)} when one of them is absent` : ''}`;
}

function listOf(fields = []) {
  if (fields.length <= 1) return `DE ${fields[0] ?? ''}`;
  return `DE ${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}`;
}
